import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import EstimateCalculator from '@/pages/estimate/components/EstimateCalculator';
import { useEstimateSnapshot, clearEstimateSnapshot } from '@/hooks/useEstimateStore';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';
const FORM_URL = 'https://readdy.ai/api/form/d785urprcamcd36dau0g';

export default function EstimatePage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { snapshot, clear } = useEstimateSnapshot();
  const sizeRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const [snapshotApplied, setSnapshotApplied] = useState(false);

  useEffect(() => {
    document.title = '예상 견적 안내 | 필그린 인테리어 강남 아파트 견적';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', '필그린 인테리어 예상 견적 안내. 아파트 평당 120만원~, 빌라 100만원~, 오피스텔 90만원~. 온라인 견적 계산기로 예상 비용 확인 후 신청.');
    setMeta('og:title', '예상 견적 안내 | 필그린 인테리어 강남 아파트 견적', true);
    setMeta('og:url', `${SITE_URL}/estimate`, true);

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '필그린 인테리어 예상 견적 안내',
      description: '아파트·빌라·오피스텔 인테리어 예상 견적 계산기 및 견적 신청.',
      url: `${SITE_URL}/estimate`,
      provider: {
        '@type': 'LocalBusiness',
        name: '필그린 인테리어',
        url: SITE_URL,
        telephone: '010-5350-7926',
      },
    };

    const scriptId = 'jsonld-estimate';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = scriptId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  // Auto-fill form when snapshot is available
  useEffect(() => {
    if (snapshot && !snapshotApplied) {
      // Fill size field
      if (sizeRef.current) {
        sizeRef.current.value = `${snapshot.pyeong}평`;
      }
      // Build message from selected items
      if (messageRef.current) {
        const lines: string[] = [`[계산기 선택 공정 - ${snapshot.pyeong}평 기준]`];
        const grouped: Record<string, string[]> = {};
        snapshot.selectedItems.forEach((item) => {
          if (!grouped[item.groupName]) grouped[item.groupName] = [];
          grouped[item.groupName].push(`${item.itemLabel} (약 ${item.price.toLocaleString()}만원)`);
        });
        Object.entries(grouped).forEach(([group, items]) => {
          lines.push(`\n▶ ${group}`);
          items.forEach((it) => lines.push(`  · ${it}`));
        });
        lines.push(`\n예상 견적: ${snapshot.totalMin.toLocaleString()}만원 ~ ${snapshot.totalMax.toLocaleString()}만원`);
        messageRef.current.value = lines.join('\n');
      }
      setSnapshotApplied(true);
    }
  }, [snapshot, snapshotApplied]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch(FORM_URL, { method: 'POST', body: data, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      setSubmitted(true);
      clear();
    } catch {
      setSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearSnapshot = () => {
    clearEstimateSnapshot();
    setSnapshotApplied(false);
    if (sizeRef.current) sizeRef.current.value = '';
    if (messageRef.current) messageRef.current.value = '';
  };

  return (
    <div className="bg-[#f7f5f3] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/38189a94261ac792bb86bd695310ec59.jpg"
            alt="견적"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-12 w-full">
          <span className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase font-medium">Estimate</span>
          <h1 className="text-3xl md:text-5xl font-light text-white mt-2 leading-snug md:leading-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>예상 견적 안내</h1>
        </div>
      </section>

      {/* Calculator Section */}
      <EstimateCalculator />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">

        {/* Estimate Form */}
        <div id="estimate-form" className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1a1a1a] leading-snug" style={{ fontFamily: "'Noto Serif KR', serif" }}>견적 신청</h2>
            <p className="text-[#666] text-sm mt-3">정확한 견적을 위해 아래 양식을 작성해주세요. feel green 담당자가 1~2 영업일 내 연락드립니다.</p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-[#e8e4dc]">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#c9a84c]/10 mx-auto mb-4">
                <i className="ri-check-line text-[#c9a84c] text-3xl" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a1a1a] mb-2">견적 신청이 완료되었습니다!</h3>
              <p className="text-[#666] text-sm">1~2 영업일 내에 담당자가 연락드리겠습니다.</p>
            </div>
          ) : (
            <>
              {/* Snapshot Preview Banner */}
              {snapshot && snapshotApplied && (
                <div className="mb-6 bg-[#0d0d0d] rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#c9a84c]/20">
                        <i className="ri-magic-line text-[#c9a84c] text-sm" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">계산기 선택 항목이 자동 입력됐어요</p>
                        <p className="text-xs text-white/50 mt-0.5">아래 신청서에 공정 내역과 평수가 채워졌습니다</p>
                      </div>
                    </div>
                    <button
                      onClick={handleClearSnapshot}
                      className="text-xs text-white/40 hover:text-white/70 cursor-pointer transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      초기화
                    </button>
                  </div>

                  {/* Selected items summary */}
                  <div className="bg-white/5 rounded-xl p-4 mb-4">
                    <div className="flex flex-wrap gap-2">
                      {snapshot.selectedItems.map((item) => (
                        <span
                          key={item.itemLabel}
                          className="inline-flex items-center gap-1 bg-white/10 text-white/80 text-xs px-2.5 py-1 rounded-full"
                        >
                          <i className="ri-check-line text-[#c9a84c] text-xs" />
                          {item.itemLabel}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-home-line text-[#c9a84c] text-xs" />
                        </div>
                        <span className="text-xs text-white/60">{snapshot.pyeong}평</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-price-tag-3-line text-[#c9a84c] text-xs" />
                        </div>
                        <span className="text-xs text-white/60">{snapshot.selectedItems.length}개 공정</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-[#c9a84c]">
                        {snapshot.totalMin.toLocaleString()}만원 ~ {snapshot.totalMax.toLocaleString()}만원
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-10 border border-[#e8e4dc] space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">이름 *</label>
                    <input name="name" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="홍길동" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">연락처 *</label>
                    <input name="phone" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="010-0000-0000" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">이메일</label>
                  <input name="email" type="email" className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="example@email.com" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">시공 유형 *</label>
                    <select name="type" required className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] bg-white cursor-pointer">
                      <option value="">선택해주세요</option>
                      {['아파트', '빌라', '오피스텔', '상업공간', '사무실', '기타'].map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-2">
                      평수 *
                      {snapshot && snapshotApplied && (
                        <span className="ml-2 text-xs text-[#c9a84c] font-normal">계산기에서 자동 입력됨</span>
                      )}
                    </label>
                    <input
                      ref={sizeRef}
                      name="size"
                      required
                      className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors ${
                        snapshot && snapshotApplied ? 'border-[#c9a84c] bg-[#fdf9f0]' : 'border-[#ddd]'
                      }`}
                      placeholder="예: 32평"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">주소</label>
                  <input name="address" className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c]" placeholder="서울시 강남구 ..." />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">시공 희망 시기</label>
                  <select name="schedule" className="w-full border border-[#ddd] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] bg-white cursor-pointer">
                    <option value="">선택해주세요</option>
                    {['1개월 이내', '1~3개월', '3~6개월', '6개월 이후', '미정'].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#333] mb-2">
                    요청사항
                    {snapshot && snapshotApplied && (
                      <span className="ml-2 text-xs text-[#c9a84c] font-normal">선택 공정 내역 자동 입력됨</span>
                    )}
                  </label>
                  <textarea
                    ref={messageRef}
                    name="message"
                    maxLength={500}
                    rows={snapshot && snapshotApplied ? 8 : 4}
                    className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#c9a84c] resize-none transition-colors ${
                      snapshot && snapshotApplied ? 'border-[#c9a84c] bg-[#fdf9f0]' : 'border-[#ddd]'
                    }`}
                    placeholder="시공 관련 요청사항을 자유롭게 작성해주세요. (최대 500자)"
                  />
                  <p className="text-xs text-[#aaa] mt-1 text-right">최대 500자</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#c9a84c] text-[#0d0d0d] font-bold py-4 rounded-full text-sm cursor-pointer hover:bg-[#b8973b] transition-colors whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? '제출 중...' : '견적 신청하기'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
